import {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
  ChangeEvent,
} from 'react';
import styled from 'styled-components';
import { ChevronIcon } from '@gif/shared-ui';
import { getColor, noop } from '@gif/shared-global';

import { Themes } from '../../types';

export interface DropdownOptions {
  value: string | number;
  label: string | number;
}

export interface DropdownProps
  extends Omit<React.ComponentProps<'select'>, 'ref'> {
  options?: DropdownOptions[];
  className?: string;
  ariaLabel?: string;
  placeholder?: string;
  message?: string;
  hasError?: boolean;
  hasValid?: boolean;
  theme?: Themes;
}

const black = getColor('black');
const white = getColor('white');

const themeColors = {
  light: black,
  dark: getColor('neutral100'),
};

const backgrounds = {
  light: white,
  dark: black,
};

const borders = {
  light: '#D2D2D2',
  dark: white,
};

const placeholders = {
  light: '#808080',
  dark: '#d3d3d3',
};

const messages = {
  light: '#57606c',
  dark: '#d3d3d3',
};

const defaultHeight = '2.4rem';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const SelectContainer = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  height: 100%;
`;

const Select = styled.select<{
  $hasError?: boolean;
  $hasValid?: boolean;
  $isPlaceholderSelected?: boolean;
  $theme: Themes;
}>`
  display: block;
  width: 100%;
  min-height: ${defaultHeight};
  padding: 0 3rem 0 var(--space-space200, 1rem);
  background-color: ${({ $theme }) => backgrounds[$theme]};
  border: 1px solid ${({ $theme }) => borders[$theme]};
  border-radius: 0.125rem;
  color: ${({ $isPlaceholderSelected, $theme }) =>
    $isPlaceholderSelected ? placeholders[$theme] : themeColors[$theme]};
  font-size: 0.84rem;
  appearance: none;
  word-break: normal;
  cursor: pointer;
  box-sizing: border-box;
  &:hover {
    opacity: 0.8;
  }
  ${({ $hasValid }) =>
    $hasValid &&
    `
    border-color: #00FF00;
  `};
  ${({ $hasError }) =>
    $hasError &&
    `
    border-color: #FF0000;
  `};
`;

const IconContainer = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 1rem;
  display: flex;
  min-height: ${defaultHeight};
  align-items: center;
  pointer-events: none;
`;

const StyledChevronIcon = styled(ChevronIcon)<{
  $isPlaceholderSelected?: boolean;
  $theme: Themes;
}>`
  width: var(--space-space300, 1.5rem);
  height: var(--space-space300, 1.5rem);
  color: ${({ $isPlaceholderSelected, $theme }) =>
    $isPlaceholderSelected ? placeholders[$theme] : themeColors[$theme]};
`;

const MessageContainer = styled.div<{
  $isPlaceholderSelected?: boolean;
  $theme: Themes;
}>`
  font-size: 0.875rem;
  p {
    margin: 0.5rem 0 0 0;
    color: ${({ $theme }) => messages[$theme]};
  }
`;

export const Dropdown = forwardRef<HTMLSelectElement, DropdownProps>(
  (props, ref) => {
    const {
      options = [],
      className,
      ariaLabel,
      placeholder,
      message,
      value,
      hasError,
      hasValid,
      onChange = noop,
      theme = Themes.LIGHT,
      ...rest
    } = props;
    const [isPlaceholderSelected, setIsPlaceholderSelected] = useState(
      placeholder !== '' && (value === '' || typeof value === 'undefined')
    );
    const hasMounted = useRef(false);
    useEffect(() => {
      if (!hasMounted.current) {
        return;
      }
      const isEmptyPlaceholder =
        placeholder === '' || typeof placeholder === 'undefined';
      if (isEmptyPlaceholder) {
        setIsPlaceholderSelected(false);
      }
      if (isEmptyPlaceholder || typeof value === 'undefined') {
        return;
      }
      setIsPlaceholderSelected(value === '');
    }, [value, placeholder]);
    useEffect(() => {
      hasMounted.current = true;
      return () => {
        hasMounted.current = false;
      };
    }, []);
    const onChangeHandler = useCallback(
      (event: ChangeEvent<HTMLSelectElement>) => {
        setIsPlaceholderSelected(false);
        onChange(event);
      },
      [onChange]
    );
    if (options.length < 1 || !ariaLabel) {
      return null;
    }
    return (
      <Container className={className}>
        <SelectContainer>
          <Select
            {...rest}
            $hasError={hasError}
            $hasValid={hasValid}
            $isPlaceholderSelected={isPlaceholderSelected}
            $theme={theme}
            ref={ref}
            aria-label={ariaLabel}
            {...(typeof value === 'undefined' && { defaultValue: '' })} // allow for placeholder to work on uncontrolled component
            {...(typeof value !== 'undefined' && { value })} // use the `value` prop only for controlled components
            onChange={onChangeHandler}
          >
            {placeholder && (
              <option key="" value="" disabled>
                {placeholder}
              </option>
            )}
            {options?.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
          <IconContainer>
            <StyledChevronIcon
              $isPlaceholderSelected={isPlaceholderSelected}
              $theme={theme}
            />
          </IconContainer>
        </SelectContainer>
        <MessageContainer
          $isPlaceholderSelected={isPlaceholderSelected}
          $theme={theme}
        >
          {message && <p aria-live="assertive">{message}</p>}
        </MessageContainer>
      </Container>
    );
  }
);

export default Dropdown;
